/* eslint-disable max-lines */
import { Card, Divider, Input, Text, Title, useModalState } from "@clipboard-health/ui-components";
import { getTheme } from "@clipboard-health/ui-theme";
import { isDefined } from "@clipboard-health/util-ts";
import { zodResolver } from "@hookform/resolvers/zod";
// eslint-disable-next-line no-restricted-imports -- TextField is needed for multiline input
import { Alert, Stack, TextField, ThemeProvider } from "@mui/material";
import { useFacilityNotes } from "@src/appV2/Facilities/api/useFacilityNotes";
import { useToast } from "@src/appV2/lib";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { DEFAULT_TIMEZONE } from "@src/appV2/Shifts/Shift/constants";
import { ExtraWorkedTimeType } from "@src/appV2/Shifts/Shift/types";
import { useGetFacilityBreakConfiguration } from "@src/appV2/Shifts/Shift/useGetFacilityBreakConfiguration";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { addHours, addMinutes, parseISO } from "date-fns";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useHistory, useParams } from "react-router-dom";
import SignaturePad from "react-signature-pad-wrapper";

import { PageWrapper } from "../../../components/PageWrapper";
import { StickyScrollPageHeader } from "../../../components/StickyScrollPageHeader";
import { TextButton } from "../../../components/TextButton";
import {
  type Duration,
  durationToMinutes,
  minutesToDuration,
} from "../../../DateTime/DurationInput";
import { PageContent } from "../../components/PageContent";
// TODO: Re-enable when backend endpoint is ready
// import { useValidateTimecard } from "../../MissedPunches/api/useValidateTimecard";
import { validateClockInTimestamp } from "../../MissedPunches/utils/validateClockInTimestamp";
import { validateClockOutTimestamp } from "../../MissedPunches/utils/validateClockOutTimestamp";
import { MY_SHIFTS_FULL_PATH } from "../../path";
import { IncompleteBreakConfirmationDialog } from "../../Timekeeping/mandatoryBreakDialogs/IncompleteBreakConfirmationDialog/IncompleteBreakConfirmationDialog";
import { WorkedThroughMandatoryBreakExplanationDialog } from "../../Timekeeping/mandatoryBreakDialogs/WorkedThroughMandatoryBreakExplanationDialog/WorkedThroughMandatoryBreakExplanationDialog";
import { useIsTimekeepingIntegrationsEnabled } from "../../Timekeeping/TimekeepingIntegrations/useIsTimekeepingIntegrationsEnabled";
import { FacilitySignatureSection } from "../../Timekeeping/Timesheet/components/FacilitySignatureSection";
import { ReadOnlyTimesheetTimeEntries } from "../../Timekeeping/Timesheet/components/ReadOnlyTimesheetTimeEntries";
import { TimesheetTimeEntries } from "../../Timekeeping/Timesheet/components/TimesheetTimeEntries";
import { WorkerSignatureSection } from "../../Timekeeping/Timesheet/components/WorkerSignatureSection";
import { EditBreakTimeBottomSheet } from "../../Timekeeping/Timesheet/EditBreakTimeBottomSheet";
import { EditClockInTimeBottomSheet } from "../../Timekeeping/Timesheet/EditClockInTimeBottomSheet";
import { EditClockOutTimeBottomSheet } from "../../Timekeeping/Timesheet/EditClockOutTimeBottomSheet";
import { useTimesheetEntry } from "../../Timekeeping/Timesheet/hooks/useTimesheetEntry";
import {
  type SubmitTimesheetFormValues,
  useTimesheetSubmission,
} from "../../Timekeeping/Timesheet/hooks/useTimesheetSubmission";
import { calculateAutoBreakTimes } from "../../Timekeeping/Timesheet/utils/calculateAutoBreakTimes";
import { calculateBreakDuration } from "../../Timekeeping/Timesheet/utils/calculateBreakDuration";
import { formatBreakDisplay } from "../../Timekeeping/Timesheet/utils/formatBreakDisplay";
import { isMandatoryBreakRequired } from "../../Timekeeping/Timesheet/utils/isMandatoryBreakRequired";
import { TimesheetSubmittedDialog } from "../../Timekeeping/TimesheetSubmittedDialog/TimesheetSubmittedDialog";
import { useGetShiftDetailsData } from "../api/useGetShiftDetailsData";
import { SubmitUnverifiedTimesheetConfirmationBottomSheet } from "./Dialogs/SubmitUnverifiedTimesheetConfirmationBottomSheet";
import { SubmitTimesheetPageSkeleton } from "./SubmitTimesheetPageSkeleton";

interface RouteParams {
  shiftId: string;
}

const ERROR_MESSAGES = {
  INTERNAL_TIMECLOCK_DISABLED:
    "Timestamps provided from an internal time clock cannot be modified.",
  TIMESHEET_EDITING_DISABLED: "Timesheet editing is currently disabled for this facility.",
} as const;

// Non-IP timesheet uses facility-signing and worker-signing steps
// (instead of facility-signing and hcp-signing used by IP timesheets)
type UnverifiedTimesheetStep = "facility-signing" | "worker-signing";

// eslint-disable-next-line complexity, sonarjs/cognitive-complexity
export function SubmitUnverifiedTimesheetPage() {
  const { shiftId } = useParams<RouteParams>();
  const history = useHistory();
  const { showErrorToast } = useToast();
  const worker = useDefinedWorker();
  const workerName = `${worker.firstName ?? ""} ${worker.lastName ?? ""}`.trim();

  useLogEffect(
    APP_V2_USER_EVENTS.TIMESHEET_SUBMISSION_PAGE_VIEWED,
    { shiftId, isNonIP: true },
    { enabled: isDefined(shiftId) }
  );

  const scrollRef = useRef<HTMLDivElement>(null);
  const signaturePadRef = useRef<SignaturePad>(null);
  const editClockInModalState = useModalState();
  const editClockOutModalState = useModalState();
  const editBreakModalState = useModalState();
  const workedThroughMandatoryBreakExplanationModalState = useModalState();
  const incompleteBreakConfirmationModalState = useModalState();
  const timesheetSubmittedModalState = useModalState();
  const submitUnverifiedTimesheetConfirmationModalState = useModalState();

  const {
    shift,
    shiftState,
    shiftError,
    isLoading: isShiftDataLoading,
    breakPolicyNoteId,
  } = useGetShiftDetailsData({
    shiftId: shiftId ?? "",
  });

  const formTimesheet = useTimesheetEntry(shift);
  const workplaceTimezone = shift?.facility?.tmz ?? DEFAULT_TIMEZONE;
  const isCaliforniaFacility = shift?.facility?.featureSettings?.californiaTimesheet ?? false;

  // Step management: California facilities require facility signature first, then worker signature
  const [currentStep, setCurrentStep] = useState<UnverifiedTimesheetStep>(
    isCaliforniaFacility ? "facility-signing" : "worker-signing"
  );

  const { data: facilityNotesData } = useFacilityNotes(shift?.facilityId ?? "", {
    enabled: isDefined(shift?.facilityId) && isDefined(breakPolicyNoteId),
  });

  const breakPolicyNote = facilityNotesData?.find((note) => note.noteId === breakPolicyNoteId);

  const { mandatoryBreakDurationInMinutes } = useGetFacilityBreakConfiguration({
    workplaceId: shift?.facility?.userId,
  });

  const timekeepingIntegrationsConfig = useIsTimekeepingIntegrationsEnabled({
    config: shiftState?.metadata.externalTimekeepingIntegrationsConfig,
  });
  const isTimekeepingIntegrationsEnabled = isDefined(timekeepingIntegrationsConfig);
  const isTimesheetEditDisabled = shiftState?.metadata.isTimeSheetEditDisabled ?? false;

  // TODO: Re-enable when backend endpoint is ready
  // const { mutateAsync: validateTimecardAPI, isLoading: isValidating } = useValidateTimecard();

  // Map unverified timesheet steps to standard timesheet steps for useTimesheetSubmission
  function getTimesheetSubmissionStep(): "facility-signing" | "hcp-signing" | undefined {
    if (currentStep === "facility-signing") {
      return "facility-signing";
    }

    if (isCaliforniaFacility && currentStep === "worker-signing") {
      return "hcp-signing";
    }

    return undefined;
  }

  const {
    handleSubmit: handleTimesheetSubmit,
    isLoading: isSubmissionLoading,
    formSchema,
  } = useTimesheetSubmission({
    shift,
    isCaliforniaFacility,
    currentStep: getTimesheetSubmissionStep(),
    isUnverifiedTimesheetFlow: true,
    actualClockInOut:
      shift?.clockInOut?.start && shift?.clockInOut?.end
        ? { start: shift.clockInOut.start, end: shift.clockInOut.end }
        : undefined,
    onSuccess: async () => {
      logEvent(APP_V2_USER_EVENTS.TIMESHEET_SUBMITTED, {
        shiftId: shiftId ?? "",
        isCaliforniaFacility,
        step: currentStep,
        isNonIP: true,
      });
      submitUnverifiedTimesheetConfirmationModalState.openModal();
    },
    onError: (errorMessage) => {
      logEvent(APP_V2_USER_EVENTS.TIMESHEET_SUBMISSION_FAILED, {
        shiftId: shiftId ?? "",
        isCaliforniaFacility,
        step: currentStep,
        errorMessage,
        isNonIP: true,
      });
      showErrorToast(errorMessage);
    },
  });

  // Used when the requested break change is LESS than the mandatoryBreakDurationInMinutes
  const pendingBreakChange = useRef<
    { breakStartTime: Date | undefined; breakDuration: Duration } | undefined
  >(undefined);

  const { control, handleSubmit, reset, setValue, watch, formState } =
    useForm<SubmitTimesheetFormValues>({
      resolver: zodResolver(formSchema),
      defaultValues: {
        clockInTime: new Date(),
        clockOutTime: new Date(),
        breakStartTime: undefined,
        breakEndTime: undefined,
        missedPunches: [{ type: ExtraWorkedTimeType.FULL_SHIFT, reason: "" }],
        breakAuthorizationReason: undefined,
        // California facility fields
        facilityEmployeeName: "",
        facilityEmployeeSignature: "",
        workerStation: "",
        workerNursingAssignment: "",
        hcpName: "",
        hcpSignature: "",
      },
      mode: "onChange",
    });

  const { isValid } = formState;

  const formClockInTime = watch("clockInTime");
  const formClockOutTime = watch("clockOutTime");
  const formBreakStartTime = watch("breakStartTime");
  const formBreakEndTime = watch("breakEndTime");
  const formBreakAuthorizationReason = watch("breakAuthorizationReason");

  const [
    didFacilityEmployeeAcknowledgeBreakAuthorizationReason,
    setDidFacilityEmployeeAcknowledgeBreakAuthorizationReason,
  ] = useState(false);

  // Check if mandatory break is required for the shift
  const isMandatoryBreakRequiredForShift = useMemo(
    () =>
      isDefined(shift) && isDefined(shiftState) && shift.clockInOut?.start && shift.clockInOut?.end
        ? isMandatoryBreakRequired({
            requiresLunchBreak: shift.facility.requiresLunchBreak ?? false,
            solveUnpaidBreaksRolloutStageWithTargetingConfig:
              shiftState.metadata.solveUnpaidBreaksRolloutStageWithTargetingConfig,
            clockInTime: parseISO(shift.clockInOut.start),
            clockOutTime: parseISO(shift.clockInOut.end),
          })
        : false,
    [shift, shiftState]
  );

  const isMandatoryBreakRequiredForCurrentFormValues = useMemo(() => {
    if (!isDefined(shift) || !isDefined(shiftState) || !formClockInTime || !formClockOutTime) {
      return false;
    }

    return isMandatoryBreakRequired({
      requiresLunchBreak: shift.facility.requiresLunchBreak ?? false,
      solveUnpaidBreaksRolloutStageWithTargetingConfig:
        shiftState.metadata.solveUnpaidBreaksRolloutStageWithTargetingConfig,
      clockInTime: formClockInTime,
      clockOutTime: formClockOutTime,
    });
  }, [formClockInTime, formClockOutTime, shift, shiftState]);

  // Initialize form once shift data is available
  const formInitialized = useRef(false);
  useEffect(() => {
    if (
      formInitialized.current ||
      !isDefined(shift) ||
      !isDefined(isMandatoryBreakRequiredForShift)
    ) {
      return;
    }

    const clockInTime = formTimesheet?.clockInTime ?? parseISO(shift.start);
    const clockOutTime = formTimesheet?.clockOutTime ?? parseISO(shift.end);

    let breakStartTime = formTimesheet?.breakStartTime;
    let breakEndTime = formTimesheet?.breakEndTime;

    // Auto-add mandatory break if not present
    if (!breakStartTime && !breakEndTime && isMandatoryBreakRequiredForShift) {
      const { autoBreakStartTime, autoBreakEndTime } = calculateAutoBreakTimes(
        clockInTime,
        clockOutTime,
        mandatoryBreakDurationInMinutes
      );
      breakStartTime = autoBreakStartTime;
      breakEndTime = autoBreakEndTime;
    }

    reset({
      clockInTime,
      clockOutTime,
      breakStartTime,
      breakEndTime,
      missedPunches: [{ type: ExtraWorkedTimeType.FULL_SHIFT, reason: "" }],
      breakAuthorizationReason: undefined,
      // California facility fields
      facilityEmployeeName: "",
      facilityEmployeeSignature: "",
      workerStation: "",
      workerNursingAssignment: "",
      hcpName: `${worker.firstName ?? ""} ${worker.lastName ?? ""}`.trim(),
      hcpSignature: "",
    });

    formInitialized.current = true;
  }, [
    shift,
    formTimesheet,
    isMandatoryBreakRequiredForShift,
    mandatoryBreakDurationInMinutes,
    reset,
    worker.firstName,
    worker.lastName,
  ]);

  // displayTimesheet is always defined once we have form values
  const displayTimesheet = useMemo(
    () => ({
      clockInTime: formClockInTime,
      clockOutTime: formClockOutTime,
      breakStartTime: formBreakStartTime,
      breakEndTime: formBreakEndTime,
    }),
    [formClockInTime, formClockOutTime, formBreakStartTime, formBreakEndTime]
  );

  const breakDisplayText = formatBreakDisplay({
    displayTimesheet,
    workplaceTimezone,
  });

  // Validation logic
  const clockInValidation = useMemo(() => {
    if (!isDefined(shift) || !isDefined(shiftState) || !formClockInTime) {
      return { isValid: true };
    }

    const isGeofenceFacility = isDefined(shift.geofence?.entry);
    const isNfcOrMeshFacility = Boolean(shift.nfcTag);

    return validateClockInTimestamp({
      selectedTime: formClockInTime,
      geofenceArrivalTime: shift.geofence?.entry ? parseISO(shift.geofence.entry) : undefined,
      shiftStartAt: parseISO(shift.start),
      facilitySettings: {
        preventEarlyClockIns: !(shiftState.metadata.isEarlyClockInEnabled ?? false),
        earlyClockInMinutes: shiftState.metadata.earlyClockInEnabledInMinutes,
      },
      workerClockIn: shift.clockInOut?.start ? parseISO(shift.clockInOut.start) : undefined,
      clockOutTime: formClockOutTime,
      breakStartTime: formBreakStartTime,
      isGeofenceFacility,
      isNfcOrMeshFacility,
    });
  }, [shift, shiftState, formClockInTime, formClockOutTime, formBreakStartTime]);

  const clockOutValidation = useMemo(() => {
    if (!isDefined(shift) || !formClockInTime || !formClockOutTime) {
      return { isValid: true };
    }

    const isGeofenceFacility = isDefined(shift.geofence?.entry);
    const isNfcOrMeshFacility = Boolean(shift.nfcTag);
    const isDirectIntegrationFacility = isDefined(
      shiftState?.metadata.externalTimekeepingIntegrationsConfig
    );

    return validateClockOutTimestamp({
      selectedTime: formClockOutTime,
      clockInTime: formClockInTime,
      geofenceDepartureTime: shift.geofence?.exit ? parseISO(shift.geofence.exit) : undefined,
      shiftEndAt: parseISO(shift.end),
      workerClockOut: shift.clockInOut?.end ? parseISO(shift.clockInOut.end) : undefined,
      isGeofenceFacility,
      isNfcOrMeshFacility,
      isDirectIntegrationFacility,
    });
  }, [shift, shiftState, formClockInTime, formClockOutTime]);

  const hasBlockingErrors = !clockInValidation.isValid || !clockOutValidation.isValid;

  // Determine if time entries are read-only (during worker signing for California)
  const areTimeEntriesReadOnly = isCaliforniaFacility && currentStep === "worker-signing";

  const isEditingAllowed =
    currentStep === "facility-signing" ||
    (currentStep === "worker-signing" && !isCaliforniaFacility);

  // Check if facility signature should be disabled pending confirmations
  const isFacilitySignaturePadDisabled =
    isSubmissionLoading ||
    (isDefined(formBreakAuthorizationReason) &&
      formBreakAuthorizationReason.length > 0 &&
      !didFacilityEmployeeAcknowledgeBreakAuthorizationReason);

  function getTimesheetHeaderText() {
    if (isTimekeepingIntegrationsEnabled) {
      return "The following times have been provided by the facility's internal time clock and cannot be changed here.";
    }

    if (isTimesheetEditDisabled) {
      return "Submit your timesheet now to get paid instantly.";
    }

    return "Make sure the times are correct and make any edits before submitting.";
  }

  function showEditingDisabledErrorToast(message: string) {
    showErrorToast(message);
  }

  function applyBreakChange(newBreakStartTime: Date | undefined, newBreakDuration: Duration) {
    setValue("breakStartTime", newBreakStartTime, { shouldValidate: true });

    // Calculate break end time from start time and duration
    if (newBreakDuration.hours === 0 && newBreakDuration.minutes === 0) {
      // Break is skipped
      setValue("breakEndTime", undefined, { shouldValidate: true });
    } else if (newBreakStartTime) {
      // Calculate end time from start time + duration
      let endTime = new Date(newBreakStartTime);
      if (newBreakDuration.hours > 0) {
        endTime = addHours(endTime, newBreakDuration.hours);
      }

      if (newBreakDuration.minutes > 0) {
        endTime = addMinutes(endTime, newBreakDuration.minutes);
      }

      setValue("breakEndTime", endTime, { shouldValidate: true });
    } else {
      // No start time, so no end time
      setValue("breakEndTime", undefined, { shouldValidate: true });
    }
  }

  // Handler for signature pad updates (non-California facility employee signature)
  const onSignatureUpdate = useCallback(() => {
    const signature = signaturePadRef.current?.toDataURL();
    if (signature) {
      setValue("facilityEmployeeSignature", signature, { shouldValidate: true });
    }
  }, [setValue]);

  // Attach signature pad event listener for non-California worker signature
  useEffect(() => {
    if (!isCaliforniaFacility) {
      const signaturePad = signaturePadRef.current;
      signaturePad?.instance?.addEventListener("endStroke", onSignatureUpdate);
      return () => {
        signaturePad?.instance?.removeEventListener("endStroke", onSignatureUpdate);
      };
    }

    return undefined;
  }, [isCaliforniaFacility, onSignatureUpdate]);

  const onSubmit = useCallback(
    async (data: SubmitTimesheetFormValues) => {
      // For California facilities: handle two-step signature flow
      if (isCaliforniaFacility && currentStep === "facility-signing") {
        // Facility employee has signed, show "pass phone back" dialog
        timesheetSubmittedModalState.openModal();
        return;
      }

      // TODO: Enable backend validation when endpoint is ready
      // Step 1: Validate with backend API
      // await validateTimecardAPI({
      //   shiftId,
      //   data: {
      //     submitClockInOut: {
      //       start: data.clockInTime.toISOString(),
      //       end: data.clockOutTime.toISOString(),
      //     },
      //     submitLunchInOut:
      //       data.breakStartTime && data.breakEndTime
      //         ? {
      //             start: data.breakStartTime.toISOString(),
      //             end: data.breakEndTime.toISOString(),
      //           }
      //         : undefined,
      //   },
      // });

      // Final submission (worker-signing step or non-California)
      await handleTimesheetSubmit(data);
    },
    [isCaliforniaFacility, currentStep, timesheetSubmittedModalState, handleTimesheetSubmit]
  );

  return (
    <ThemeProvider theme={getTheme()}>
      <PageWrapper
        sx={{ backgroundColor: (theme) => theme.palette.background.tertiary, paddingX: 0 }}
      >
        <StickyScrollPageHeader
          title="Shift verification request"
          scrollContainerRef={scrollRef}
          headerVariant="tertiary"
          hideBackButton={isCaliforniaFacility && currentStep === "worker-signing"}
        />

        <PageContent ref={scrollRef} sx={{ paddingX: 0 }}>
          {isShiftDataLoading && <SubmitTimesheetPageSkeleton />}

          {shiftError ? (
            <Alert severity="error" sx={{ mt: 4, mx: 6 }}>
              Something went wrong while loading this shift. Please try again.
            </Alert>
          ) : undefined}

          {!isShiftDataLoading && !shiftError && shift && (
            <form onSubmit={handleSubmit(onSubmit)}>
              <Stack spacing={6} sx={{ paddingX: 6, paddingBottom: 6 }}>
                {/* Blocking Error Banners */}
                {hasBlockingErrors && (
                  <Alert severity="error">
                    {clockInValidation.message ?? clockOutValidation.message}
                  </Alert>
                )}

                {/* Time Entries - Different component based on step */}
                {areTimeEntriesReadOnly ? (
                  <ReadOnlyTimesheetTimeEntries
                    displayTimesheet={displayTimesheet}
                    workplaceTimezone={workplaceTimezone}
                    formatBreakDisplay={() => breakDisplayText}
                  />
                ) : (
                  <>
                    {/* Mandatory Break Info */}
                    {!isTimekeepingIntegrationsEnabled &&
                      !isTimesheetEditDisabled &&
                      isMandatoryBreakRequiredForCurrentFormValues && (
                        <Alert severity="info" sx={{ mb: 4 }}>
                          This facility requires you to take a {mandatoryBreakDurationInMinutes}{" "}
                          minute break if you work more than 5 hours. If you didn&apos;t take a
                          break, please edit the time below.
                        </Alert>
                      )}

                    {/* Header */}
                    <Text variant="body1" sx={{ color: (theme) => theme.palette.text.secondary }}>
                      {getTimesheetHeaderText()}
                    </Text>

                    <TimesheetTimeEntries
                      hidePencilIcons={isTimekeepingIntegrationsEnabled || isTimesheetEditDisabled}
                      displayTimesheet={displayTimesheet}
                      workplaceTimezone={workplaceTimezone}
                      formatBreakDisplay={() => breakDisplayText}
                      onEditBreak={() => {
                        logEvent(APP_V2_USER_EVENTS.TIMESHEET_EDIT_BREAK_TAPPED, {
                          shiftId,
                        });

                        if (isTimekeepingIntegrationsEnabled) {
                          showEditingDisabledErrorToast(ERROR_MESSAGES.INTERNAL_TIMECLOCK_DISABLED);
                        } else if (isTimesheetEditDisabled) {
                          showEditingDisabledErrorToast(ERROR_MESSAGES.TIMESHEET_EDITING_DISABLED);
                        } else if (isEditingAllowed) {
                          editBreakModalState.openModal();
                        }
                      }}
                      onEditClockIn={() => {
                        logEvent(APP_V2_USER_EVENTS.TIMESHEET_EDIT_CLOCK_IN_TAPPED, {
                          shiftId,
                        });

                        if (isTimekeepingIntegrationsEnabled) {
                          showEditingDisabledErrorToast(ERROR_MESSAGES.INTERNAL_TIMECLOCK_DISABLED);
                        } else if (isTimesheetEditDisabled) {
                          showEditingDisabledErrorToast(ERROR_MESSAGES.TIMESHEET_EDITING_DISABLED);
                        } else if (isEditingAllowed) {
                          editClockInModalState.openModal();
                        }
                      }}
                      onEditClockOut={() => {
                        logEvent(APP_V2_USER_EVENTS.TIMESHEET_EDIT_CLOCK_OUT_TAPPED, {
                          shiftId,
                        });

                        if (isTimekeepingIntegrationsEnabled) {
                          showEditingDisabledErrorToast(ERROR_MESSAGES.INTERNAL_TIMECLOCK_DISABLED);
                        } else if (isTimesheetEditDisabled) {
                          showEditingDisabledErrorToast(ERROR_MESSAGES.TIMESHEET_EDITING_DISABLED);
                        } else if (isEditingAllowed) {
                          editClockOutModalState.openModal();
                        }
                      }}
                    />
                  </>
                )}

                {/*
                  Single reason field for unverified timesheets (non-IP shifts).
                  For non-IP, there's no hcp_clock_in/out to compare against, so we send FULL_SHIFT type.

                  Future (FOR-1315): Per-timestamp bottom sheets for IP missed punch flow with
                  visual indicators and granular pending amounts per timestamp.
                */}
                <Stack spacing={2}>
                  <Text variant="body2">Note to workplace*</Text>
                  <Controller
                    name="missedPunches.0.reason"
                    control={control}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        multiline
                        required
                        fullWidth
                        placeholder="Explain what happened"
                        rows={4}
                        error={Boolean(fieldState.error)}
                        helperText={
                          fieldState.error?.message ?? `${field.value?.length ?? 0}/500 characters`
                        }
                        inputProps={{ maxLength: 500 }}
                      />
                    )}
                  />
                </Stack>
              </Stack>

              {/* California Facility Signature Section */}
              {isCaliforniaFacility && currentStep === "facility-signing" && (
                <FacilitySignatureSection
                  control={control}
                  firstName={worker.firstName ?? ""}
                  isCaliforniaFacility={isCaliforniaFacility}
                  signaturePadIsDisabled={isFacilitySignaturePadDisabled}
                  isValid={isValid}
                  setValue={setValue}
                  onDisabledSignaturePadTouchStart={() => {
                    // Check for break authorization
                    const shouldShowIncompleteBreakConfirmation =
                      isDefined(formBreakAuthorizationReason) &&
                      formBreakAuthorizationReason.length > 0 &&
                      !didFacilityEmployeeAcknowledgeBreakAuthorizationReason;

                    if (shouldShowIncompleteBreakConfirmation) {
                      incompleteBreakConfirmationModalState.openModal();
                    }
                  }}
                  onSubmit={handleSubmit(onSubmit)}
                />
              )}

              {/* California Worker Signature Section */}
              {isCaliforniaFacility && currentStep === "worker-signing" && (
                <>
                  <WorkerSignatureSection
                    control={control}
                    firstName={worker.firstName ?? ""}
                    fullName={workerName}
                    workerStation={watch("workerStation") ?? ""}
                    workerNursingAssignment={watch("workerNursingAssignment") ?? ""}
                    signaturePadIsDisabled={isSubmissionLoading}
                    setValue={setValue}
                  />

                  <LoadingButton
                    fullWidth
                    type="submit"
                    size="large"
                    variant="contained"
                    disabled={!isValid}
                    isLoading={isSubmissionLoading}
                    onClick={handleSubmit(onSubmit)}
                  >
                    Request workplace verification
                  </LoadingButton>
                </>
              )}

              {/* Non-California: Simple signature */}
              {!isCaliforniaFacility && (
                <Stack
                  spacing={6}
                  sx={{
                    paddingX: 6,
                    paddingY: 6,
                    backgroundColor: (theme) => theme.palette.background.paper,
                  }}
                >
                  <Stack spacing={4}>
                    <Title semibold variant="h6" component="h6">
                      Signature
                    </Title>
                    <Text variant="body2" sx={{ color: (theme) => theme.palette.text.tertiary }}>
                      Please sign below to confirm the accuracy of the times you&apos;ve entered
                    </Text>

                    {/* Facility Employee Name */}
                    <Stack spacing={2}>
                      <Text variant="body2">Facility employee full name*</Text>
                      <Controller
                        name="facilityEmployeeName"
                        control={control}
                        render={({ field }) => (
                          <Input {...field} placeholder="Enter your full name" />
                        )}
                      />
                    </Stack>

                    {/* Facility Employee Signature */}
                    <Stack spacing={2}>
                      <Text variant="body2">Facility employee signature*</Text>
                      <Card outlined sx={{ padding: 5 }}>
                        <Controller
                          name="facilityEmployeeSignature"
                          control={control}
                          render={() => (
                            <SignaturePad ref={signaturePadRef} redrawOnResize height={180} />
                          )}
                        />
                        <Divider
                          sx={{
                            borderColor: (theme) => theme.palette.border?.primary,
                            marginBottom: 4,
                          }}
                        />
                        <Stack direction="row" justifyContent="flex-end">
                          <TextButton
                            underline={false}
                            onClick={() => {
                              signaturePadRef.current?.clear();
                              setValue("facilityEmployeeSignature", "", { shouldValidate: true });
                            }}
                          >
                            Clear
                          </TextButton>
                        </Stack>
                      </Card>
                    </Stack>
                  </Stack>

                  {/* Submit Button */}
                  <LoadingButton
                    fullWidth
                    size="large"
                    type="submit"
                    variant="contained"
                    disabled={hasBlockingErrors || !isValid}
                    isLoading={isSubmissionLoading}
                  >
                    Request workplace verification
                  </LoadingButton>
                </Stack>
              )}
            </form>
          )}
        </PageContent>

        {/* Edit Modals - Only available when editing is allowed */}
        {isEditingAllowed && isDefined(shift) && isDefined(shiftState) && (
          <>
            <EditClockInTimeBottomSheet
              modalState={editClockInModalState}
              workplaceTimezone={workplaceTimezone}
              shiftStartAt={parseISO(shift.start)}
              isNfcShift={isDefined(shift.nfcTag) && shift.nfcTag}
              earliestGeofenceEntryTime={
                shift.geofence?.entry ? parseISO(shift.geofence.entry) : undefined
              }
              clockInButtonTappedAt={formTimesheet?.clockInTime}
              earlyClockInEnabled={shiftState.metadata.isEarlyClockInEnabled ?? false}
              earlyClockInEnabledInMinutes={shiftState.metadata.earlyClockInEnabledInMinutes}
              clockInTime={formClockInTime}
              clockOutTime={formClockOutTime}
              breakStartTime={formBreakStartTime}
              workerClockIn={shift.clockInOut?.start ? parseISO(shift.clockInOut.start) : undefined}
              isGeofenceFacility={isDefined(shift.geofence?.entry)}
              isMissedPunchRequestEnabled={shiftState.metadata.isMissedPunchRequestEnabled}
              onConfirm={(newClockInTime) => {
                setValue("clockInTime", newClockInTime, { shouldValidate: true });
              }}
            />

            <EditClockOutTimeBottomSheet
              modalState={editClockOutModalState}
              clockOutTime={formClockOutTime}
              workplaceTimezone={workplaceTimezone}
              clockOutButtonTappedAt={formTimesheet?.clockOutTime}
              breakEndTime={formBreakEndTime}
              clockInTime={formClockInTime}
              shiftEndAt={parseISO(shift.end)}
              geofenceDepartureTime={
                shift.geofence?.exit ? parseISO(shift.geofence.exit) : undefined
              }
              workerClockOut={shift.clockInOut?.end ? parseISO(shift.clockInOut.end) : undefined}
              isGeofenceFacility={isDefined(shift.geofence?.entry)}
              isNfcOrMeshFacility={Boolean(shift.nfcTag)}
              isDirectIntegrationFacility={isDefined(
                shiftState.metadata.externalTimekeepingIntegrationsConfig
              )}
              isMissedPunchRequestEnabled={shiftState.metadata.isMissedPunchRequestEnabled}
              onConfirm={(newClockOutTime) => {
                setValue("clockOutTime", newClockOutTime, { shouldValidate: true });
              }}
            />

            <EditBreakTimeBottomSheet
              modalState={editBreakModalState}
              breakStartTime={formBreakStartTime}
              defaultBreakDuration={minutesToDuration(mandatoryBreakDurationInMinutes)}
              breakDuration={calculateBreakDuration(formBreakStartTime, formBreakEndTime)}
              initialBreakSkipped={!isDefined(formBreakStartTime)}
              workplaceTimezone={workplaceTimezone}
              clockInTime={formClockInTime}
              clockOutTime={formClockOutTime}
              onConfirm={(newBreakStartTime, newBreakDuration) => {
                if (
                  isMandatoryBreakRequiredForCurrentFormValues &&
                  durationToMinutes(newBreakDuration) < mandatoryBreakDurationInMinutes
                ) {
                  workedThroughMandatoryBreakExplanationModalState.openModal();
                  pendingBreakChange.current = {
                    breakStartTime: newBreakStartTime,
                    breakDuration: newBreakDuration,
                  };
                } else {
                  setValue("breakAuthorizationReason", undefined);
                  applyBreakChange(newBreakStartTime, newBreakDuration);
                }
              }}
            />
          </>
        )}

        {/* Mandatory Break Dialog */}
        {isDefined(shift) && isMandatoryBreakRequiredForCurrentFormValues && (
          <WorkedThroughMandatoryBreakExplanationDialog
            modalState={workedThroughMandatoryBreakExplanationModalState}
            breakPolicy={breakPolicyNote?.note ?? ""}
            workplaceId={shift.facilityId}
            workplaceName={shift.facility.name}
            mandatoryBreakDurationInMinutes={mandatoryBreakDurationInMinutes}
            onSuccess={(explanation) => {
              setValue("breakAuthorizationReason", explanation);

              if (pendingBreakChange.current) {
                applyBreakChange(
                  pendingBreakChange.current.breakStartTime,
                  pendingBreakChange.current.breakDuration
                );
                pendingBreakChange.current = undefined;
              }

              workedThroughMandatoryBreakExplanationModalState.closeModal();
            }}
          />
        )}

        {/* Incomplete Break Confirmation Dialog */}
        {formBreakAuthorizationReason ? (
          <IncompleteBreakConfirmationDialog
            modalState={incompleteBreakConfirmationModalState}
            professionalStatement={formBreakAuthorizationReason}
            onContinue={() => {
              setDidFacilityEmployeeAcknowledgeBreakAuthorizationReason(true);
              incompleteBreakConfirmationModalState.closeModal();
            }}
          />
        ) : null}

        {/* Timesheet Submitted Dialog (Pass Phone Back) */}
        <TimesheetSubmittedDialog
          modalState={timesheetSubmittedModalState}
          isCaliforniaFacility={isCaliforniaFacility}
          onContinue={async () => {
            timesheetSubmittedModalState.closeModal();

            if (isCaliforniaFacility) {
              setCurrentStep("worker-signing");
              // Scroll to top for worker signature section
              scrollRef.current?.scrollTo({ top: 0, behavior: "smooth" });
            }
          }}
        />

        {/* Confirmation Bottom Sheet */}
        <SubmitUnverifiedTimesheetConfirmationBottomSheet
          modalState={submitUnverifiedTimesheetConfirmationModalState}
          onContinue={() => {
            history.replace(MY_SHIFTS_FULL_PATH);
          }}
        />
      </PageWrapper>
    </ThemeProvider>
  );
}
/* eslint-enable max-lines */
